<template>
    <div
        ref="anchor"
        :class="['dropdown enhanced-dropdown', { focused: isListOpen }]"
    >
        <input
            v-if="typable"
            :id="id"
            ref="input"
            v-model="text"
            type="text"
            :class="['input-no-border', { 'out-of-focus': !isListOpen }]"
            :placeholder="modelValue ? modelValue.name : ''"
            @click="onInputClick"
            @keydown.enter.prevent="onInputEnter"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.space="onSpace"
            @keydown.esc="onEsc"
        />
        <div
            v-else
            :id="id"
            tabindex="0"
            class="input-no-border"
            @click="onInputClick"
            @keydown.enter.prevent="onInputEnter"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.space="onSpace"
            @keydown.esc="onEsc"
        >
            {{ modelValue.name }}
        </div>
        <teleport to="body">
            <div
                v-if="isListOpen"
                ref="list"
                v-focus-out="onCloseList"
                :class="[
                    'list-wrapper',
                    { 'list-wrapper--flipped': isListFlipped },
                ]"
                :style="listStyle"
                @click="onListWrapperClick()"
            >
                <ul>
                    <li
                        v-if="nullable"
                        :class="{ highlighted: highlightedName === '' }"
                        @click="onSelectEmptyOption()"
                        @mouseover="highlightedName = ''"
                    >
                        &nbsp;
                    </li>
                    <li
                        v-for="(option, index) in filteredOptions"
                        :key="`option-${index}`"
                        :class="{
                            highlighted: highlightedName === option.name,
                        }"
                        @click="onSelectOption(option)"
                        @mouseover="highlightedName = option.name"
                    >
                        {{ option.name }}
                    </li>
                    <template v-if="typable && insertable">
                        <li
                            v-for="(option, index) in filteredNewOptions"
                            :key="`new-option-${index}`"
                            :class="{
                                highlighted: highlightedName === option.name,
                            }"
                            @click="onSelectNewOption(option)"
                            @mouseover="highlightedName = option.name"
                        >
                            {{ option.name }}
                        </li>
                        <li
                            v-if="text.trim().length && isOptionUnique"
                            :class="[
                                'new-entry',
                                { highlighted: highlightedName === text },
                            ]"
                            @click="onAddOption()"
                            @mouseover="highlightedName = text"
                        >
                            <span>{{ uppercaseText }}</span>
                            <span class="new-entry-text">(new)</span>
                        </li>
                        <li
                            v-if="!text.length && newPromptText"
                            class="add-new-prompt"
                        >
                            <span>
                                {{ newPromptText }}
                            </span>
                        </li>
                    </template>
                    <li v-else-if="!filteredOptions.length" class="no-matches">
                        No matches found.
                    </li>
                </ul>
            </div>
        </teleport>
    </div>
</template>
<script>
import BaseAdvancedDropdown from "js/components/base/BaseAdvancedDropdown.js";

export default {
    name: "EnhancedDropdown",
    extends: BaseAdvancedDropdown,
    props: {
        modelValue: {
            type: Object,
            default: () => {
                return {};
            },
        },
        nullable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            isListOpen: false,
            highlightedName: "",
            text: "",
            newOptions: [],
        };
    },
    computed: {
        keyboardOptions() {
            const names = [];
            if (this.nullable) {
                names.push("");
            }
            names.push(...this.filteredOptions.map((o) => o.name));
            if (this.typable && this.insertable) {
                names.push(...this.filteredNewOptions.map((o) => o.name));
                if (this.text.trim().length && this.isOptionUnique) {
                    names.push(this.text);
                }
            }

            return names;
        },
    },
    watch: {
        text() {
            this.highlightedName = "";
        },
    },
    methods: {
        onCloseList() {
            this.isListOpen = false;
            this.highlightedName = "";

            if (this.$refs.input !== undefined) {
                this.$refs.input.blur();
            }

            if (this.text.length) {
                this.text = "";
                this.$emit("update:modelValue", { name: "" });
            }
        },
        onEsc() {
            this.onCloseList();
        },
        onSelectOption(option) {
            this.text = "";
            this.$emit("update:modelValue", option);
            this.onCloseList();
        },
        onSelectNewOption(option) {
            this.text = "";
            this.$emit("update:modelValue", option);
            this.onCloseList();
        },
        onSelectEmptyOption() {
            this.text = "";
            this.$emit("update:modelValue", null);
            this.onCloseList();
        },
        onAddOption() {
            this.text = this.text.trim();

            if (this.text.length && this.isOptionUnique) {
                const newOption = { name: this.uppercaseText };
                this.text = "";
                this.newOptions.push(newOption);
                this.$emit("update:modelValue", newOption);
                this.onCloseList();
            }
        },
        onInputClick() {
            this.isListOpen = true;
            this.highlightedName = this.modelValue ? this.modelValue.name : "";
        },
        onInputEnter(event) {
            event.preventDefault();

            if (!this.isListOpen) {
                this.onInputClick();

                return;
            }

            if (this.highlightedName) {
                this.confirmHighlighted();

                return;
            }

            if (this.typable && this.insertable) {
                this.onAddOption();
            }
        },
        onArrowDown() {
            if (!this.isListOpen) {
                this.onInputClick();
            }

            this.highlightedName = this.stepHighlighted(
                this.keyboardOptions,
                this.highlightedName,
                1
            );
        },
        onArrowUp() {
            if (!this.isListOpen) {
                this.onInputClick();
            }

            this.highlightedName = this.stepHighlighted(
                this.keyboardOptions,
                this.highlightedName,
                -1
            );
        },
        onSpace(event) {
            if (this.typable && !this.highlightedName) {
                return;
            }

            event.preventDefault();

            if (!this.isListOpen) {
                this.onInputClick();

                return;
            }

            this.confirmHighlighted();
        },
        confirmHighlighted() {
            if (this.nullable && this.highlightedName === "") {
                this.onSelectEmptyOption();

                return;
            }

            const existing =
                this.filteredOptions.find(
                    (o) => o.name === this.highlightedName
                ) ??
                (this.typable && this.insertable
                    ? this.filteredNewOptions.find(
                          (o) => o.name === this.highlightedName
                      )
                    : undefined);

            if (existing) {
                this.onSelectOption(existing);

                return;
            }

            if (
                this.typable &&
                this.insertable &&
                this.highlightedName === this.text &&
                this.text.trim().length &&
                this.isOptionUnique
            ) {
                this.onAddOption();
            }
        },
    },
};
</script>
